"use client";

import React from "react";
import { useRouter } from "next/navigation";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  ResponsiveAlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";
import { Phone, Settings } from "lucide-react";
import type { NoNumberReason } from "@/app/_module/hooks/usePhoneNumberValidation";
import type { DialBlocker } from "@/app/_module/hooks/useDialBlocker";
import { noNumberCopy, numbersSettingsPath } from "@/app/_module/components/NoActiveNumberNotice";

interface NoPhoneNumberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason?: NoNumberReason;
  expired?: { phoneNumber: string; expiredAt: string | null } | null;
  /** Full blocker from useDialBlocker (payment / trial / subscription / number). Wins over reason/expired. */
  blocker?: DialBlocker | null;
}

export function NoPhoneNumberDialog({ open, onOpenChange, reason = null, expired = null, blocker = null }: NoPhoneNumberDialogProps) {
  const router = useRouter();
  const copy = blocker ? { title: blocker.title, body: blocker.body, cta: blocker.cta } : noNumberCopy(reason, expired);
  const href = blocker ? blocker.href : numbersSettingsPath();

  const handleGoToSettings = () => {
    onOpenChange(false);
    if (href.startsWith("mailto:")) window.location.href = href;
    else router.push(href);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveAlertDialogContent size="md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-orange-100 rounded-full">
              <Phone className="h-6 w-6 text-orange-600" />
            </div>
            <AlertDialogTitle className="text-xl">{copy.title}</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-base space-y-3">
            <p>{copy.body}</p>
            <p className="text-sm text-muted-foreground">
              Phone numbers are used as your caller ID when making calls to leads. Without a purchased
              number, calls cannot be placed.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleGoToSettings}
            className="bg-primary hover:bg-primary/90"
          >
            <Settings className="h-4 w-4 mr-2" />
            {copy.cta}
          </AlertDialogAction>
        </AlertDialogFooter>
      </ResponsiveAlertDialogContent>
    </AlertDialog>
  );
}
